// Was S1 vom Baum annimmt, und die Uebersetzung in die heutige Suche
// (ST-SEA-002, „PQL S1“; CM-SEA-011 Teil, CM-SEA-015 Teil).
//
// S1 aendert die Suchroute nicht: angenommen wird nur, was die heutige Form
// {term, filter} ausdruecken kann und was der Filterbereich als Chip zeigen
// kann (E2b a, „die Chips sind die eine Wahrheit“). Alles andere ist ein
// sperrender Hinweis mit Stelle, nie eine still andere Suche:
//
// - ODER nur innerhalb eines Feldes, das mehrere Werte traegt;
// - Ausschluss nur als -status:inaktiv (D1, Dirigent -76 12.10.2026), weil der
//   Filterbereich nur „Inaktive einbeziehen“ kennt;
// - dasselbe Feld nur einmal: zwei Stichwoerter ohne ODER kaemen als ODER an,
//   weil die Facette ihre Werteliste mit ODER verknuepft;
// - ein Freitext: der Server kennt einen Teilstring.

import { isAnd, isField, isNot, isOr, isText, type FieldNode, type QueryNode, type Span, type TextNode } from './baum.ts';
import { printFreeText, printValue } from './drucker.ts';
import {
  MAXIMUM_LOCALITY_LENGTH,
  MINIMUM_LOCALITY_LENGTH,
  POSTAL_CODE_PATTERN,
  fieldByName,
  roleCodeFor,
  similarRoleSpelling,
  statusCodeFor,
  type FieldKey,
} from './felder.ts';
import { lex, parseQuery, type HintCode, type QueryHint } from './syntax.ts';
import { ROLE_LABELS, roleLabelSpellings } from '../../context-roles.ts';
import { searchTermProblem } from '../../parties.ts';

/** Ein Stichwort, wie getippt; aufgeloest wird es erst beim Abschicken ueber S0. */
export interface KeywordName {
  readonly name: string;
  /** Aus der Schreibweise mit Achse: stichwort:"Presse (Branche)". */
  readonly axis?: string | undefined;
  readonly span?: Span | undefined;
}

export interface S1Form {
  readonly freeText?: string | undefined;
  readonly keywords: readonly KeywordName[];
  readonly houseName?: string | undefined;
  /** Wo das Haus im Suchtext steht, fuer den Cursor eines Hinweises der Aufloesung. */
  readonly houseSpan?: Span | undefined;
  readonly roleCodes: readonly string[];
  readonly postalCodePrefix?: string | undefined;
  readonly locality?: string | undefined;
  /** -status:inaktiv getippt: Inaktive ausblenden, auch wenn sie im Filter einbezogen waren. */
  readonly excludeInactive: boolean;
}

export interface S1Result {
  /** Nur gesetzt, wenn kein Hinweis sperrt. */
  readonly form: S1Form | undefined;
  readonly hints: readonly QueryHint[];
}

const AXIS_SPELLING = /^(.+?)\s*\(([^()]+)\)$/u;

export function keywordName(value: string, span?: Span): KeywordName {
  const match = AXIS_SPELLING.exec(value.trim());
  return match === null
    ? { name: value.trim(), span }
    : { name: (match[1] as string).trim(), axis: (match[2] as string).trim(), span };
}

function flatten(node: QueryNode, kind: 'and' | 'or'): QueryNode[] {
  if (kind === 'and' && isAnd(node)) return node.and.flatMap((child) => flatten(child, kind));
  if (kind === 'or' && isOr(node)) return node.or.flatMap((child) => flatten(child, kind));
  return [node];
}

class S1Check {
  readonly hints: QueryHint[] = [];
  private readonly seen = new Set<FieldKey>();
  private readonly texts: TextNode[] = [];
  private readonly keywords: KeywordName[] = [];
  private readonly roleCodes: string[] = [];
  private houseName: string | undefined;
  private houseSpan: Span | undefined;
  private postalCodePrefix: string | undefined;
  private locality: string | undefined;
  private excludeInactive = false;
  private readonly source: string;

  constructor(source: string) {
    this.source = source;
  }

  private block(code: HintCode, span: Span, extra: { suggestion?: string; subject?: string; quote?: string } = {}): void {
    const { quote, ...rest } = extra;
    this.hints.push({ code, span, quote: quote ?? this.source.slice(span.from, span.to), blocking: true, ...rest });
  }

  visit(node: QueryNode): void {
    if (isText(node)) {
      this.texts.push(node);
    } else if (isField(node)) {
      this.field(node, node.values);
    } else if (isNot(node)) {
      this.negated(node.not, node.span);
    } else if (isOr(node)) {
      this.alternatives(node.span, flatten(node, 'or'));
    } else if (isAnd(node)) {
      for (const child of flatten(node, 'and')) this.visit(child);
    }
  }

  /** ODER: nur Werte desselben Feldes, das mehrere Werte traegt. */
  private alternatives(span: Span, branches: readonly QueryNode[]): void {
    const fields = branches.filter(isField);
    const first = fields[0];
    const sameField =
      first !== undefined &&
      fields.length === branches.length &&
      fields.every((branch) => branch.field === first.field && branch.op === 'equals');
    if (!sameField) {
      this.block('or_across_fields', span);
      return;
    }
    this.field(
      { ...first, span },
      fields.flatMap((branch) => branch.values),
    );
  }

  private field(node: FieldNode, values: FieldNode['values']): void {
    const definition = fieldByName(node.field);
    const subject = node.fieldSpelling;
    if (definition === undefined) {
      this.block('field_later', node.span, { subject });
      return;
    }
    if (node.op !== 'equals') {
      this.block('comparison_unsupported', node.span, { subject });
      return;
    }
    if (this.seen.has(definition.key)) {
      this.block('field_twice', node.span, { subject });
      return;
    }
    this.seen.add(definition.key);
    if (!definition.multiValued && values.length > 1) {
      this.block('several_values', node.span, { subject });
      return;
    }
    const single = values[0];
    switch (definition.key) {
      case 'stichwort':
        for (const value of values) this.keywords.push(keywordName(value.value, value.span));
        return;
      case 'haus':
        this.houseName = single?.value.trim();
        this.houseSpan = single?.span;
        return;
      case 'rolle':
        for (const value of values) {
          const code = roleCodeFor(value.value);
          if (code === undefined) {
            const suggestion = similarRoleSpelling(value.value);
            this.block('role_unknown', value.span, suggestion === undefined ? {} : { suggestion: printValue(suggestion) });
          } else if (!this.roleCodes.includes(code)) {
            this.roleCodes.push(code);
          }
        }
        return;
      case 'status':
        this.block('status_unsupported', node.span);
        return;
      case 'ort': {
        const locality = single?.value.trim() ?? '';
        if (locality.length < MINIMUM_LOCALITY_LENGTH || locality.length > MAXIMUM_LOCALITY_LENGTH) {
          this.block('locality_length', node.span);
          return;
        }
        this.locality = locality;
        return;
      }
      case 'plz': {
        const prefix = single?.value.trim() ?? '';
        if (!POSTAL_CODE_PATTERN.test(prefix)) {
          this.block('postal_code_form', node.span);
          return;
        }
        this.postalCodePrefix = prefix;
        return;
      }
    }
  }

  /** Ausschluss: in S1 nur -status:inaktiv. */
  private negated(node: QueryNode, span: Span): void {
    if (isField(node) && fieldByName(node.field)?.key === 'status' && node.op === 'equals') {
      const codes = node.values.map((value) => statusCodeFor(value.value));
      if (codes.length === 1 && codes[0] === 'inactive' && !this.seen.has('status')) {
        this.seen.add('status');
        this.excludeInactive = true;
        return;
      }
      this.block('status_unsupported', span);
      return;
    }
    this.block('not_open_field', span, isField(node) ? { subject: node.fieldSpelling } : {});
  }

  finish(): S1Form | undefined {
    if (this.texts.length > 1) {
      const second = this.texts[1] as TextNode;
      this.block('text_split', second.span);
    }
    const text = this.texts[0];
    if (text !== undefined && searchTermProblem(text.text) !== undefined) {
      this.block('text_length', text.span);
    }
    const narrows =
      this.keywords.length > 0 ||
      this.houseName !== undefined ||
      this.roleCodes.length > 0 ||
      this.postalCodePrefix !== undefined ||
      this.locality !== undefined;
    if (text === undefined && !narrows && !this.hints.some((hint) => hint.blocking)) {
      this.block('no_search_reason', { from: 0, to: this.source.length });
    }
    if (this.hints.some((hint) => hint.blocking)) return undefined;
    return {
      freeText: text?.text,
      keywords: this.keywords,
      houseName: this.houseName,
      houseSpan: this.houseSpan,
      roleCodes: this.roleCodes,
      postalCodePrefix: this.postalCodePrefix,
      locality: this.locality,
      excludeInactive: this.excludeInactive,
    };
  }
}

/**
 * Greift die Suchsprache? Nur wenn der Text eine Angabe (`feld:`) oder ein
 * grossgeschriebenes Operatorwort traegt. Jede andere Eingabe ist die heutige
 * Namenssuche und laeuft unveraendert: „Müller (Hamburg)“ oder ein Name mit
 * Anfuehrungszeichen aendern ihre Bedeutung nicht (E4 c).
 */
export function usesQuerySyntax(source: string): boolean {
  return lex(source).some((token) => token.kind === 'field' || token.kind === 'or' || token.kind === 'and' || token.kind === 'not');
}

/**
 * Prueft einen Suchtext fuer S1. Sperrende Hinweise des Parsers gehen vor:
 * solange die Eingabe nicht gelesen werden kann, sagt S1 nichts dazu.
 * Ein leeres Feld ergibt den heutigen Satz der Mindestlaenge.
 */
export function checkForS1(source: string): S1Result {
  const parsed = parseQuery(source);
  if (parsed.tree === undefined && parsed.hints.length === 0) {
    return {
      form: undefined,
      hints: [{ code: 'text_length', span: { from: 0, to: source.length }, quote: '', blocking: true }],
    };
  }
  if (parsed.hints.some((hint) => hint.blocking) || parsed.tree === undefined) {
    return { form: undefined, hints: parsed.hints };
  }
  const check = new S1Check(source);
  check.visit(parsed.tree);
  const form = check.finish();
  const hints = [...parsed.hints, ...check.hints].sort((a, b) => a.span.from - b.span.from);
  return { form, hints };
}

/**
 * Die Rolle als lesbares Wort fuer den Suchtext: die ausgeschriebene Form
 * („Autorin“), nicht die Doppelpunktform -- sie liest der Parser ohne
 * Anfuehrungszeichen nicht als einen Wert.
 */
function roleSpelling(code: string): string {
  const label = ROLE_LABELS[code];
  if (label === undefined) return code;
  const formen = roleLabelSpellings(label);
  return formen[1] ?? formen[0] ?? code;
}

/**
 * Der Rueckweg „Als Suchtext kopieren“: die Angaben des Filterbereichs als
 * Suchtext, den checkForS1 wieder genau so liest (Roundtrip).
 */
export function s1FormToText(form: S1Form): string {
  const parts: string[] = [];
  if (form.freeText !== undefined) parts.push(printFreeText(form.freeText));
  const keywordValues = form.keywords.map((keyword) =>
    printValue(keyword.axis === undefined ? keyword.name : `${keyword.name} (${keyword.axis})`),
  );
  if (keywordValues.length === 1) parts.push(`stichwort:${keywordValues[0]}`);
  if (keywordValues.length > 1) parts.push(`stichwort:(${keywordValues.join(' ODER ')})`);
  if (form.houseName !== undefined) parts.push(`haus:${printValue(form.houseName)}`);
  const roles = form.roleCodes.map((code) => printValue(roleSpelling(code)));
  if (roles.length === 1) parts.push(`rolle:${roles[0]}`);
  if (roles.length > 1) parts.push(`rolle:(${roles.join(' ODER ')})`);
  if (form.postalCodePrefix !== undefined) parts.push(`plz:${form.postalCodePrefix}`);
  if (form.locality !== undefined) parts.push(`ort:${printValue(form.locality)}`);
  if (form.excludeInactive) parts.push('-status:inaktiv');
  return parts.join(' ');
}

